import React, {useEffect, useState} from 'react';
import {CircularProgress} from '@mui/material';
import FieldHelpSuffix from '../common/FieldHelpSuffix';
import {useTranslation} from 'react-i18next';
import {ActionButton, CancelButton} from '../common/ActionButtons';
import {ValidationError} from '../../utils/validation';
import {validateRequiredTrimmed} from '../../utils/required-validation';
import {
    InputField,
    StyledDialog,
    StyledDialogActions,
    StyledDialogContent,
    StyledDialogTitle
} from '../data-sources/CreateDataSourceDialog.styled';

type ValidateFnType = (value: string) => ValidationError | null;

interface GenericInputDialogProps {
    open: boolean;
    title: string;
    label: string;
    initialValue?: string;
    helperText?: string;
    validate?: ValidateFnType;
    loading?: boolean;
    confirmButtonText?: string;
    cancelButtonText?: string;

    placeholder?: string;
    required?: boolean;
    multiline?: boolean;
    rows?: number;
    type?: React.HTMLInputTypeAttribute;

    onClose: () => void;
    onConfirm: (value: string) => void;
}

const GenericInputDialog: React.FC<GenericInputDialogProps> = ({
                                                                   open,
                                                                   title,
                                                                   label,
                                                                   initialValue = '',
                                                                   helperText,
                                                                   validate,
                                                                   loading = false,
                                                                   placeholder,
                                                                   confirmButtonText = 'common.save',
                                                                   cancelButtonText = 'common.cancel',
                                                                   required = false,
                                                                   multiline = false,
                                                                   rows = 3,
                                                                   type = 'text',
                                                                   onClose,
                                                                   onConfirm
                                                               }) => {
    const { t } = useTranslation();
    const [value, setValue] = useState(initialValue);
    const [touched, setTouched] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    useEffect(() => {
        if (open) {
            setValue(initialValue);
            setTouched(false);
            setSubmitted(false);
        }
    }, [open, initialValue]);

    const isEmptyInvalid = required && !!validateRequiredTrimmed(value, 'inputDialog.required');

    const runValidation = (val: string): ValidationError | null => {
        const requiredError = required ? validateRequiredTrimmed(val, 'inputDialog.required') : null;
        if (requiredError) return requiredError;
        return validate ? validate(val) : null;
    };
    const validationError = runValidation(value);
    const visibleError = (touched || submitted) ? validationError : null;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const next = e.target.value;
        setValue(next);
    };

    const handleSubmit = () => {
        setSubmitted(true);
        if (validationError) {
            return;
        }
        onConfirm(value.trim());
    };

    const handleKeyDown: React.KeyboardEventHandler = (e) => {
        if (e.key === 'Enter' && !multiline && !loading) {
            e.preventDefault();
            handleSubmit();
        }
    };

    const disabledConfirm = loading || isEmptyInvalid || !!visibleError;

    return (
        <StyledDialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="generic-input-dialog-title">
            <StyledDialogTitle id="generic-input-dialog-title">{title}</StyledDialogTitle>

            <StyledDialogContent>
                <InputField
                    label={label}
                    variant="outlined"
                    fullWidth
                    value={value}
                    onChange={handleChange}
                    onBlur={() => setTouched(true)}
                    onKeyDown={handleKeyDown}
                    error={!!visibleError}
                    placeholder={placeholder}
                    required={required}
                    multiline={multiline}
                    rows={multiline ? rows : undefined}
                    type={type}
                    helperText={visibleError ? String(t(visibleError.key)) : (helperText || "")}
                    inputProps={{
                        'aria-label': String(label),
                    }}
                    InputProps={{
                        endAdornment: (
                            <FieldHelpSuffix
                                asAdornment
                                title={t('inputDialog.help.value')}
                                ariaLabel={t('inputDialog.help.value') as string}
                            />
                        )
                    }}
                />
            </StyledDialogContent>

            <StyledDialogActions>
                <CancelButton onClick={onClose} disabled={loading}>
                    {t(cancelButtonText)}
                </CancelButton>
                <ActionButton onClick={handleSubmit} disabled={disabledConfirm}>
                    {loading ? <CircularProgress size={24} color="inherit" aria-hidden={true}/> : t(confirmButtonText)}
                </ActionButton>
            </StyledDialogActions>
        </StyledDialog>
    );
};

export default GenericInputDialog;
